import React, { useMemo, useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const toLocal = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const BotAppointmentsPanel = ({
  botId,
  botName,
  token,
  appointments,
  loadingAppointments,
  calendarError,
  onRefresh,
  onCreated,
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [date, setDate] = useState(''); // yyyy-mm-dd
  const [startTime, setStartTime] = useState(''); // HH:MM
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(false);

  const canCreate = useMemo(
    () =>
      !!botId &&
      !!token &&
      customerName.trim() &&
      customerEmail.trim() &&
      date &&
      startTime &&
      endTime,
    [botId, token, customerName, customerEmail, date, startTime, endTime]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!canCreate) return;

    // Compose ISO times in UTC (assume user input is local and treat as UTC for simplicity)
    const startIso = `${date}T${startTime}:00Z`;
    const endIso = `${date}T${endTime}:00Z`;

    setCreating(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bots/${botId}/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_name: customerName.trim(),
          customer_email: customerEmail.trim(),
          customer_phone: customerPhone.trim() || undefined,
          start_time: startIso,
          end_time: endIso,
          notes: notes.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || 'Failed to create appointment');
      }
      toast.success('Appointment created in calendar');
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
      setDate('');
      setStartTime('');
      setEndTime('');
      setNotes('');
      onCreated?.(data);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to create appointment');
    } finally {
      setCreating(false);
    }
  };

  const handleConnectCalendar = async () => {
    if (!token || connectingCalendar) return;
    setConnectingCalendar(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/google/login`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.authorization_url) {
        throw new Error(data?.detail || 'Failed to start Google Calendar connection');
      }
      window.open(data.authorization_url, '_blank', 'noopener,noreferrer');
      toast.success('Google Calendar authorization opened in a new tab');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to start Google Calendar connection');
    } finally {
      setConnectingCalendar(false);
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <CalendarClock className="w-3.5 h-3.5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">Appointments</h2>
            <p className="text-[11px] text-zinc-500">
              Create and track calendar bookings for <span className="text-zinc-200 font-medium">{botName || 'this bot'}</span>
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[11px] text-zinc-400 hover:text-white"
          onClick={onRefresh}
          data-testid="bot-appointments-refresh-button"
        >
          {loadingAppointments ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-3 text-xs border-zinc-700 bg-zinc-950/70"
          onClick={handleConnectCalendar}
          disabled={!token || connectingCalendar}
          data-testid="bot-appointments-connect-calendar-button"
        >
          {connectingCalendar ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
          Connect Google Calendar
        </Button>
        <p className="text-[11px] text-zinc-500">
          Use this once after saving your Google keys in Settings.
        </p>
      </div>

      {calendarError && (
        <p className="text-[11px] text-amber-400 mb-2">
          Google Calendar is not fully configured yet: {calendarError}. You can still collect appointments via leads.
        </p>
      )}

      <form onSubmit={handleCreate} className="space-y-2 text-xs">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Customer Name</label>
            <Input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              placeholder="Jane Doe"
              data-testid="bot-appointments-customer-name-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Customer Email</label>
            <Input
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              placeholder="jane@example.com"
              type="email"
              data-testid="bot-appointments-customer-email-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Date</label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              data-testid="bot-appointments-date-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Start Time</label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              data-testid="bot-appointments-start-time-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">End Time</label>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              data-testid="bot-appointments-end-time-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Customer Phone (optional)</label>
            <Input
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              placeholder="+10000000000"
              data-testid="bot-appointments-customer-phone-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Notes (optional)</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-violet-500"
              placeholder="Context for the call"
              data-testid="bot-appointments-notes-input"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            className="h-8 px-3 text-xs bg-violet-600 hover:bg-violet-500"
            disabled={!canCreate || creating}
            data-testid="bot-appointments-create-button"
          >
            {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create Test Appointment'}
          </Button>
        </div>
      </form>

      <div className="mt-2 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-zinc-950/70 border-b border-zinc-800 text-[11px] text-zinc-400 flex items-center justify-between">
          <span>Upcoming Appointments</span>
        </div>
        <div className="max-h-48 overflow-y-auto">
          {loadingAppointments ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500 p-3">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading appointments...
            </div>
          ) : appointments && appointments.length > 0 ? (
            <Table className="w-full text-[11px]">
              <TableHeader className="bg-zinc-900/70">
                <TableRow>
                  <TableHead className="px-3 py-2 text-left">Customer</TableHead>
                  <TableHead className="px-3 py-2 text-left">When</TableHead>
                  <TableHead className="px-3 py-2 text-left">Email</TableHead>
                  <TableHead className="px-3 py-2 text-left">Calendar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments.map((a) => (
                  <TableRow key={a.id} className="border-t border-zinc-800">
                    <TableCell className="px-3 py-2 whitespace-nowrap">{a.customer_name}</TableCell>
                    <TableCell className="px-3 py-2 whitespace-nowrap">
                      {toLocal(a.start_time)}
                    </TableCell>
                    <TableCell className="px-3 py-2 whitespace-nowrap">{a.customer_email}</TableCell>
                    <TableCell className="px-3 py-2 whitespace-nowrap">
                      {a.html_link ? (
                        <a
                          href={a.html_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-violet-400 hover:underline"
                        >
                          Open
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[11px] text-zinc-500 p-3">No appointments yet for this bot.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BotAppointmentsPanel;
